
import FooterMobile from "@/components/FooterMobile.vue";
import LotItem from "@/components/LotItem.vue";
import HeaderSecondary from "@/components/headers/HeaderSecondary.vue";
import SidebarTender from "@/components/sidebars/SidebarTender.vue";
import { defineComponent } from "vue";
import { mapActions, mapGetters } from "vuex";
import { ITender } from "@/interfaces/tender";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import { EventType, trackEvent } from "@/helpers/AmplitudeHelper";
import { decodeBase64Url, decodeJwtToken } from "@/helpers/APIHelper";
import { formatTenderName } from "@/helpers/FormatStringHelper";
import * as amplitude from '@amplitude/analytics-browser';

export default defineComponent({
  name: "TenderView",
  components: {
    HeaderSecondary,
    SidebarTender,
    LotItem,
    FooterMobile,
    LoadingSpinner,
  },
  data() {
    return {
      includeWinnerLink: false,
    }
  },
  methods: {
    ...mapActions(['toggleAuthModal', 'getTenderDetails', 'addFavourite', 'deleteFavourite', 'getCompany']),
    formatName(tender: ITender) {
      return formatTenderName(tender)
    },
    formatDate(dateString: string) {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear().toString();
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      switch (this.$t("tender_details.at")) {
        case "в":
          return `${day}.${month}.${year} в ${hours}:${minutes}`;
        case "at":
          return `${day}.${month}.${year} at ${hours}:${minutes}`;
        case "да":
          return `${day}.${month}.${year} ${hours}:${minutes} да`;
      }
    },
    formatFilename(fileName: string) {
      return fileName.replace(/_/g, "_<wbr/>");
    },
    prepareFileUrl(file: string) {
      const tender = this.getTender as ITender
      if (tender.source === 'xarid_uzex') {
        return tender.url
      } else {
        return JSON.parse(file).file_url
      }
    },
    handleClickCompany(event: MouseEvent, inn: string) {
      const url = `/company/${inn}?page=1`;
      if (window.innerWidth > 768) {
        window.open(url, '_blank');
        event.preventDefault();
      } else {
        this.$router.push({ path: url, query: { page: 1 }});
        event.preventDefault();
      }
    },
    async setWinnerLink() {
      const tender = this.getTender as ITender
      if (tender.winner_inn) {
        const result = await this.getCompany({ company_tin: tender.winner_inn })
        this.includeWinnerLink = result !== null;
      } else {
        this.includeWinnerLink = false
      }
    },
    async handleSavePurchase() {
      await this.addFavourite({ 
        tenderId: this.getTender.id, 
        notificationMessage: this.$t('notifications.add_favourite') 
      })

      const identifyEvent = new amplitude.Identify();
      identifyEvent.add('favourites', 1);
      amplitude.identify(identifyEvent);
      trackEvent(EventType.purchase_save, { purchase_id: this.getTender.id })
    },
    async handleDeletePurchase() {
      await this.deleteFavourite({ 
        tenderId: this.getTender.id, 
        favId: this.getTender.fav_id, 
        notificationMessage: this.$t('notifications.delete_favourite')
      })

      const identifyEvent = new amplitude.Identify();
      identifyEvent.add('favourites', -1);
      amplitude.identify(identifyEvent);
      trackEvent(EventType.purchase_delete, { purchase_id: this.getTender.id })
    },
  },
  computed: {
    ...mapGetters(['isAuthenticated', 'getTender', 'isLoading']),
    isFavourite() {
      const tender = this.getTender as ITender
      return !!tender.fav_id
    },
    isFinished() {
      const tender = this.getTender as ITender
      const endDate = new Date(tender.end_date)
      const currentDate = new Date()

      if (endDate <= currentDate) {
        return true
      }
      return false
    },
    metaDescription() {
      return `${this.$store.getters.getTender.type} в ${this.$store.getters.getTender.country}, ${this.$store.getters.getTender.region}. Закупка для ${this.$store.getters.getTender.company} на Tender.asia`
    },
    metaTitle() {
      return `${this.$store.getters.getTender.type} "${this.$store.getters.getTender.name}"`
    },
    metaCanonicalLink() {
      return `https://tender.asia${this.$route.path}`
    },
  },
  async created() {
    const bodyElementClasses = document.body.classList;
    bodyElementClasses.remove(...bodyElementClasses);
    bodyElementClasses.add("sidebar-wo-footer");
    const tenderId = this.$route.params.tenderId as string;
    const result = await this.getTenderDetails({tenderId: tenderId, numLots: 50})
    if (result === null) {
      this.$router.replace({path: '/404'})
    } else {
      if ("open_site" in this.$route.query) {
        const payload = decodeBase64Url(this.$route.query.open_site as string)
        if (payload) {
          const { user_id, username } = payload
          const token = localStorage.getItem('token');
          const token_payload = decodeJwtToken(token as string)
          if (token_payload && String(user_id) == token_payload.telegram_id) {
            trackEvent(
              EventType.open_site, 
              {link: this.getTender.url, ...(username ? { username: username } : {})},
              {...(username ? { username: username } : {})},
            )
          }
        }
        this.$router.replace({
          query: undefined,
        });
      } else {
        trackEvent(EventType.purchase_opened)
      }
    }
    await this.setWinnerLink()
  },
  head() {
    const tender = this.getTender
    if (!tender) { return {} }
    return {
      title: this.metaTitle,
      meta: [{ name: 'description', content: this.metaDescription }],
      link: [
        { rel: "canonical", href: this.metaCanonicalLink }
      ],
    }
  },
});
